import React, { useState, useEffect } from "react";
import { CVSS40 } from "@pandatix/js-cvss";
import { getVulnerabilityColour } from "../../reports/VulnerabilityFlag";
import { ButtonOptions } from "./ControlledButtonOptions";

export interface CVSS40MetricOption {
  label: string;
  value: string;
  description?: string;
}

export const METRIC_DEFINITIONS: Record<string, { name: string; options: CVSS40MetricOption[] }> = {
  AV: {
    name: "Attack Vector",
    options: [
      { label: "Network", value: "N" },
      { label: "Adjacent", value: "A" },
      { label: "Local", value: "L" },
      { label: "Physical", value: "P" }
    ]
  },
  AC: {
    name: "Attack Complexity",
    options: [
      { label: "Low", value: "L" },
      { label: "High", value: "H" }
    ]
  },
  AT: {
    name: "Attack Requirements",
    options: [
      { label: "None", value: "N" },
      { label: "Present", value: "P" }
    ]
  },
  PR: {
    name: "Privilege Required",
    options: [
      { label: "None", value: "N" },
      { label: "Low", value: "L" },
      { label: "High", value: "H" }
    ]
  },
  UI: {
    name: "User Interaction",
    options: [
      { label: "None", value: "N" },
      { label: "Passive", value: "P" },
      { label: "Active", value: "A" }
    ]
  },
  VC: {
    name: "Vulnerable System Confidentiality",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  VI: {
    name: "Vulnerable System Integrity",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  VA: {
    name: "Vulnerable System Availability",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  SC: {
    name: "Subsequent System Confidentiality",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  SI: {
    name: "Subsequent System Integrity",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  SA: {
    name: "Subsequent System Availability",
    options: [
      { label: "High", value: "H" },
      { label: "Low", value: "L" },
      { label: "None", value: "N" }
    ]
  },
  // Threat Metrics
  E: {
    name: "Exploit Maturity",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Attacked", value: "A" },
      { label: "Proof-of-Concept", value: "P" },
      { label: "Unreported", value: "U" }
    ]
  },
  // Environmental Metrics
  CR: {
    name: "Confidentiality Requirement",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "High", value: "H" },
      { label: "Medium", value: "M" },
      { label: "Low", value: "L" }
    ]
  },
  IR: {
    name: "Integrity Requirement",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "High", value: "H" },
      { label: "Medium", value: "M" },
      { label: "Low", value: "L" }
    ]
  },
  AR: {
    name: "Availability Requirement",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "High", value: "H" },
      { label: "Medium", value: "M" },
      { label: "Low", value: "L" }
    ]
  },
  // Supplemental Metrics
  S: {
    name: "Safety",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Negligible", value: "N" },
      { label: "Present", value: "P" }
    ]
  },
  AU: {
    name: "Automatable",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "No", value: "N" },
      { label: "Yes", value: "Y" }
    ]
  },
  R: {
    name: "Recovery",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Automatic", value: "A" },
      { label: "User", value: "U" },
      { label: "Irrecoverable", value: "I" }
    ]
  },
  V: {
    name: "Value Density",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Diffuse", value: "D" },
      { label: "Concentrated", value: "C" }
    ]
  },
  RE: {
    name: "Vulnerability Response Effort",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Low", value: "L" },
      { label: "Moderate", value: "M" },
      { label: "High", value: "H" }
    ]
  },
  U: {
    name: "Provider Urgency",
    options: [
      { label: "Not Defined", value: "X" },
      { label: "Clear", value: "Clear" },
      { label: "Green", value: "Green" },
      { label: "Amber", value: "Amber" },
      { label: "Red", value: "Red" }
    ]
  }
};

export interface CVSS40CalculationResult {
  version: "4.0";
  score: string;
  severity: string;
  vectorString: string;
}

export const parseCVSS40Vector = (vec?: string): Record<string, string> => {
  const result: Record<string, string> = {
    AV: "", AC: "", AT: "", PR: "", UI: "",
    VC: "", VI: "", VA: "", SC: "", SI: "", SA: "",
    E: "X", CR: "X", IR: "X", AR: "X",
    S: "X", AU: "X", R: "X", V: "X", RE: "X", U: "X"
  };
  if (!vec || !vec.startsWith("CVSS:4.0")) return result;

  const parts = vec.split("/");
  for (let i = 1; i < parts.length; i++) {
    const [k, v] = parts[i].split(":");
    if (k && v) {
      result[k.toUpperCase()] = v;
    }
  }
  return result;
};

export const buildCVSS40VectorString = (metrics: Record<string, string>): string => {
  const baseKeys = ["AV", "AC", "AT", "PR", "UI", "VC", "VI", "VA", "SC", "SI", "SA"];
  const parts = ["CVSS:4.0"];

  for (const k of baseKeys) {
    if (metrics[k]) parts.push(`${k}:${metrics[k]}`);
  }

  const optionalKeys = ["E", "CR", "IR", "AR", "S", "AU", "R", "V", "RE", "U"];
  for (const k of optionalKeys) {
    if (metrics[k] && metrics[k] !== "X") {
      parts.push(`${k}:${metrics[k]}`);
    }
  }
  return parts.join("/");
};

const CategorySelector40 = ({
  label,
  metricKey,
  value,
  onChange,
  disabled
}: {
  label: string;
  metricKey: string;
  value?: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}) => {
  const def = METRIC_DEFINITIONS[metricKey];
  if (!def) return null;

  return (
    <div className="mb-5">
      <h2 className="mb-1 text-xs font-semibold text-slate-800">{def.name || label}</h2>
      <ButtonOptions
        value={value || undefined}
        options={def.options.map(opt => ({
          label: opt.label,
          value: opt.value,
          isDisabled: disabled
        }))}
        onChange={val => val !== undefined && onChange(val)}
        className="bg-white px-2.5 py-1 text-xs"
      />
    </div>
  );
};

const getTextColor = (sev: string) => {
  switch (sev.toUpperCase()) {
    case "CRITICAL":
      return "text-ctb-red-500";
    case "HIGH":
      return "text-ctb-red-200";
    case "MEDIUM":
      return "text-ctb-yellow-200";
    case "LOW":
      return "text-ctb-green-200";
    default:
      return "text-slate-700";
  }
};

export const CVSS40Calculator: React.FC<{
  initialVector?: string;
  onChange: (result: CVSS40CalculationResult) => void;
  disabled?: boolean;
}> = ({ initialVector, onChange, disabled }) => {
  const [metrics, setMetrics] = useState<Record<string, string>>(() => {
    return parseCVSS40Vector(initialVector);
  });

  const [score, setScore] = useState<number | null>(null);
  const [severity, setSeverity] = useState<string>("None");
  const [vectorString, setVectorString] = useState<string>(initialVector || "");

  const lastEmittedVector = React.useRef<string | null>(null);

  useEffect(() => {
    if (initialVector && initialVector.startsWith("CVSS:4.0")) {
      setMetrics(parseCVSS40Vector(initialVector));
    }
  }, [initialVector]);

  useEffect(() => {
    const vecStr = buildCVSS40VectorString(metrics);
    setVectorString(vecStr);

    const baseKeys = ["AV", "AC", "AT", "PR", "UI", "VC", "VI", "VA", "SC", "SI", "SA"];
    const isComplete = baseKeys.every(k => metrics[k] && metrics[k] !== "");

    if (!isComplete) {
      setScore(null);
      setSeverity("None");
      return;
    }

    try {
      const cvss = new CVSS40(vecStr);
      const computedScore = cvss.Score();
      const rating = CVSS40.Rating(computedScore);
      const computedSeverity = rating.charAt(0) + rating.slice(1).toLowerCase();

      setScore(computedScore);
      setSeverity(computedSeverity);

      if (lastEmittedVector.current === vecStr) {
        return;
      }
      lastEmittedVector.current = vecStr;

      onChange({
        version: "4.0",
        score: computedScore.toFixed(1),
        severity: computedSeverity,
        vectorString: vecStr
      });
    } catch {
      setScore(null);
      setSeverity("None");
    }
  }, [metrics]);

  const updateMetric = (key: string, val: string) => {
    setMetrics(prev => ({ ...prev, [key]: val }));
  };

  return (
    <div className="rounded-lg bg-ctb-blue-150 p-4 transition-opacity duration-200">
      <article className="mb-3 flex items-center justify-between">
        <div className="mr-4 flex flex-col leading-tight">
          <h4 className="mb-1 text-lg font-semibold text-slate-900">
            CVSS v4.0 Vulnerability Calculator
          </h4>
          <p className="text-xs font-light text-slate-600">
            Official FIRST CVSS 4.0 specification calculator engine.
          </p>
        </div>

        <div className="flex max-h-[96px] min-h-[96px] min-w-[120px] flex-col items-center justify-center gap-0.5 rounded border border-ctb-blue-400 bg-white p-2 shadow-sm">
          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">CVSS 4.0 Score</span>
          <p className={`${score !== null ? getTextColor(severity) : "text-black"} text-2xl font-bold`}>
            {score !== null ? score.toFixed(1) : "-.-"}
          </p>
          <span className={`text-xs font-medium ${score !== null ? getTextColor(severity) : "text-slate-700"}`}>
            ({severity})
          </span>
        </div>
      </article>

      <div className="mb-3 rounded border border-slate-200 bg-slate-50 py-1.5 px-2.5 font-mono text-[11px] text-slate-700">
        <span className="font-semibold text-slate-500 mr-2">Vector:</span>
        {vectorString || "CVSS:4.0"}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <CategorySelector40 label="Attack Vector" metricKey="AV" value={metrics.AV} disabled={disabled} onChange={v => updateMetric("AV", v)} />
          <CategorySelector40 label="Attack Complexity" metricKey="AC" value={metrics.AC} disabled={disabled} onChange={v => updateMetric("AC", v)} />
          <CategorySelector40 label="Attack Requirements" metricKey="AT" value={metrics.AT} disabled={disabled} onChange={v => updateMetric("AT", v)} />
          <CategorySelector40 label="Privilege Required" metricKey="PR" value={metrics.PR} disabled={disabled} onChange={v => updateMetric("PR", v)} />
          <CategorySelector40 label="User Interaction" metricKey="UI" value={metrics.UI} disabled={disabled} onChange={v => updateMetric("UI", v)} />
        </div>

        <div>
          <CategorySelector40 label="Vulnerable System Confidentiality" metricKey="VC" value={metrics.VC} disabled={disabled} onChange={v => updateMetric("VC", v)} />
          <CategorySelector40 label="Vulnerable System Integrity" metricKey="VI" value={metrics.VI} disabled={disabled} onChange={v => updateMetric("VI", v)} />
          <CategorySelector40 label="Vulnerable System Availability" metricKey="VA" value={metrics.VA} disabled={disabled} onChange={v => updateMetric("VA", v)} />
          <CategorySelector40 label="Subsequent System Confidentiality" metricKey="SC" value={metrics.SC} disabled={disabled} onChange={v => updateMetric("SC", v)} />
          <CategorySelector40 label="Subsequent System Integrity" metricKey="SI" value={metrics.SI} disabled={disabled} onChange={v => updateMetric("SI", v)} />
          <CategorySelector40 label="Subsequent System Availability" metricKey="SA" value={metrics.SA} disabled={disabled} onChange={v => updateMetric("SA", v)} />
        </div>
      </div>
    </div>
  );
};
